import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Btn, Field, Input, Select, Modal, ORANGE } from "./ui";
import * as holiday from "../lib/holidayApi";

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
const round = (v) => Math.round(v);

// Read-only rectangle overlay used on the thumbnails.
function ZoneBox({ zone }) {
  const z = { x_pct: 50, y_pct: 50, width_pct: 60, default_font_px: 44, ...(zone || {}) };
  const h = clamp((z.default_font_px / 300) * 100 * 1.8, 10, 50);
  return (
    <div
      className="pointer-events-none absolute rounded border-2 border-[#ED8B00] bg-[#ED8B00]/20"
      style={{ left: `${z.x_pct}%`, top: `${z.y_pct}%`, width: `${z.width_pct}%`, height: `${h}%`, transform: "translate(-50%,-50%)" }}
    />
  );
}

function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(600);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((e) => setW(e[0].contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

function ZoneEditorModal({ design, onClose, onSave }) {
  const [z, setZ] = useState({
    align: "center", default_font_px: 44, min_font_px: 18, max_font_px: 60,
    x_pct: 50, y_pct: 60, width_pct: 60, ...(design.personalization_zone || {}),
  });
  const [wrapRef, width] = useWidth();
  const drag = useRef(null);

  const onMove = useCallback((e) => {
    const wrap = wrapRef.current;
    if (!drag.current || !wrap) return;
    const r = wrap.getBoundingClientRect();
    const cx = "touches" in e ? e.touches[0].clientX : e.clientX;
    const cy = "touches" in e ? e.touches[0].clientY : e.clientY;
    const px = ((cx - r.left) / r.width) * 100;
    const py = ((cy - r.top) / r.height) * 100;
    setZ((cur) => {
      if (drag.current.mode === "move") return { ...cur, x_pct: clamp(px, 2, 98), y_pct: clamp(py, 2, 98) };
      return { ...cur, width_pct: clamp(Math.abs(px - cur.x_pct) * 2, 10, 100) };
    });
  }, [wrapRef]);

  const onUp = useCallback(() => { drag.current = null; }, []);

  useEffect(() => {
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [onMove, onUp]);

  const start = (mode) => (e) => { e.preventDefault(); e.stopPropagation(); drag.current = { mode }; };
  const scale = width / 600;
  const fontPx = clamp(z.default_font_px, z.min_font_px, z.max_font_px) * scale;
  const setNum = (k) => (e) => setZ((c) => ({ ...c, [k]: Number(e.target.value) }));

  const save = () => onSave({
    ...z,
    x_pct: round(z.x_pct), y_pct: round(z.y_pct), width_pct: round(z.width_pct),
    default_font_px: round(z.default_font_px), min_font_px: round(z.min_font_px), max_font_px: round(z.max_font_px),
  });

  return (
    <Modal open onClose={onClose} title={`Safe zone · ${design.name}`} wide testId="zone-editor">
      <p className="text-sm text-slate-500">Drag the box to move it, or drag the right handle to change its width. The sample text shows the customer-facing size.</p>
      <div ref={wrapRef} className="relative mt-3 select-none overflow-hidden rounded-lg border border-slate-200 bg-slate-100" data-testid="zone-canvas">
        <img src={design.image} alt={design.name} className="block w-full" draggable={false} />
        <div
          onMouseDown={start("move")} onTouchStart={start("move")}
          data-testid="zone-drag-box"
          className="absolute cursor-move rounded border-2 border-dashed border-[#ED8B00] bg-[#ED8B00]/10"
          style={{ left: `${z.x_pct}%`, top: `${z.y_pct}%`, width: `${z.width_pct}%`, transform: "translate(-50%,-50%)" }}
        >
          <div
            className="px-1 py-1 leading-tight break-words font-semibold"
            style={{ textAlign: z.align, fontSize: `${fontPx}px`, fontFamily: "Georgia, serif", color: "#111" }}
          >
            Your message here
          </div>
          <span
            onMouseDown={start("resize")} onTouchStart={start("resize")}
            data-testid="zone-resize-handle"
            className="absolute -right-1.5 top-1/2 h-4 w-4 -translate-y-1/2 cursor-ew-resize rounded-full border-2 border-white shadow"
            style={{ backgroundColor: ORANGE }}
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label="X %"><Input type="number" value={round(z.x_pct)} onChange={setNum("x_pct")} data-testid="zone-x" /></Field>
        <Field label="Y %"><Input type="number" value={round(z.y_pct)} onChange={setNum("y_pct")} data-testid="zone-y" /></Field>
        <Field label="Width %"><Input type="number" value={round(z.width_pct)} onChange={setNum("width_pct")} data-testid="zone-w" /></Field>
        <Field label="Align">
          <Select value={z.align} onChange={(e) => setZ((c) => ({ ...c, align: e.target.value }))} data-testid="zone-align">
            <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
          </Select>
        </Field>
        <Field label="Font size"><Input type="number" value={z.default_font_px} onChange={setNum("default_font_px")} data-testid="zone-font" /></Field>
        <Field label="Min font"><Input type="number" value={z.min_font_px} onChange={setNum("min_font_px")} /></Field>
        <Field label="Max font"><Input type="number" value={z.max_font_px} onChange={setNum("max_font_px")} /></Field>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <Btn variant="ghost" onClick={onClose} data-testid="zone-cancel">Cancel</Btn>
        <Btn onClick={save} data-testid="zone-save">Save zone</Btn>
      </div>
    </Modal>
  );
}

export function HolidayZones() {
  const [coll, setColl] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    holiday.listHolidayCollections().then((d) => {
      const cs = d.collections || [];
      setColl(cs.find((x) => x.id === d.program?.active_collection_id) || cs[0] || null);
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!coll) return null;
  const pz = (coll.designs || []).filter((d) => d.personalization_enabled);
  if (pz.length === 0) return null;

  const saveZone = async (design, zone) => {
    const designs = coll.designs.map((d) => (d.id === design.id ? { ...d, personalization_zone: zone } : d));
    try {
      const updated = await holiday.updateHolidayCollection(coll.id, { designs });
      setColl(updated);
      setEditing(null);
      toast.success("Safe zone saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save (super admin required).");
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="holiday-zones-card">
      <h3 className="font-heading font-bold text-slate-900">Personalization Safe Zones</h3>
      <p className="mt-1 text-sm text-slate-500">Position the message box on each personalizable design so customer text lands in the right spot.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {pz.map((d) => (
          <button key={d.id} onClick={() => setEditing(d)} data-testid={`zone-edit-${d.id}`}
            className="group overflow-hidden rounded-lg border border-slate-200 text-left transition-colors hover:border-[#ED8B00]">
            <div className="relative bg-slate-100">
              <img src={d.image} alt={d.name} className="block w-full" />
              <ZoneBox zone={d.personalization_zone} />
            </div>
            <div className="p-2">
              <p className="text-sm font-semibold text-slate-800">{d.name}</p>
              <p className="text-xs font-medium" style={{ color: ORANGE }}>Edit zone →</p>
            </div>
          </button>
        ))}
      </div>
      {editing && <ZoneEditorModal design={editing} onClose={() => setEditing(null)} onSave={(z) => saveZone(editing, z)} />}
    </div>
  );
}
